import { ChatText, PencilSimple, Plus, ArrowRight } from '@phosphor-icons/react';
import type { TimelineEvento } from '@/types';
import { fmtDate, fmtTime, relativeTime, dayGroupLabel, contrastingText } from '@/lib/utils';
import { labelForColumn, resolveValue, resolveColor } from '@/lib/timelineResolver';
import { useCatalogs } from '@/hooks/useTasks';

// Lista de eventos timeline (comentario / cambio / creacion). Se reusa en
// el modal de la tarea (vista por-tarea) y en el feed global (vista
// global). Para el feed, el item incluye nombre de tarea encima.
//
// Props:
//   eventos: lista de eventos a renderizar
//   showTaskName: si true, muestra "en {nombreTarea}" encima de cada evento
//                 — usado por el feed; en el modal queda omitido.
//   onTaskClick: handler para abrir el modal cuando se clickea el nombre
//                de tarea (solo si showTaskName).
//   card: si true, cada evento se renderiza como tarjeta con bordes + los
//         valores de cambio como pills (usado por el feed). En el modal queda
//         el layout compacto con icono lateral.
type EventWithTask = TimelineEvento & {
  idTareaArq?: number;
  nombreTarea?: string | null;
};

type Props = {
  eventos: EventWithTask[];
  showTaskName?: boolean;
  onTaskClick?: (taskId: number) => void;
  emptyMessage?: string;
  card?: boolean;
};

export function TimelineList({
  eventos, showTaskName = false, onTaskClick, emptyMessage = 'Sin actividad', card = false,
}: Props) {
  const { data: catalogs } = useCatalogs();

  if (eventos.length === 0) {
    return <p className="text-xs text-stone-500 italic py-2">{emptyMessage}</p>;
  }

  // Modo compacto (modal): lista plana, sin agrupar por día.
  if (!card) {
    return (
      <div className="space-y-2">
        {eventos.map((e, idx) => (
          <TimelineItem
            key={`${e.tipo}-${e.fecha}-${e.idActor}-${idx}`}
            evento={e}
            catalogs={catalogs}
            showTaskName={showTaskName}
            onTaskClick={onTaskClick}
            card={false}
          />
        ))}
      </div>
    );
  }

  // Modo card (feed): agrupa por día. Los eventos vienen ordenados desc por
  // fecha, así que basta con cortar cuando cambia la etiqueta del día.
  const groups: { label: string; items: { e: EventWithTask; idx: number }[] }[] = [];
  eventos.forEach((e, idx) => {
    const label = dayGroupLabel(e.fecha);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push({ e, idx });
    else groups.push({ label, items: [{ e, idx }] });
  });

  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="sticky top-0 z-10 -mx-1 mb-2 bg-brand-bg/90 px-1 py-1 backdrop-blur">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
              {g.label}
            </span>
          </div>
          <div className="space-y-2.5">
            {g.items.map(({ e, idx }) => (
              <TimelineItem
                key={`${e.tipo}-${e.fecha}-${e.idActor}-${idx}`}
                evento={e}
                catalogs={catalogs}
                showTaskName={showTaskName}
                onTaskClick={onTaskClick}
                card
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const TIPO_META = {
  comentario: { Icon: ChatText,     chip: 'bg-blue-100 text-blue-700',       label: 'Comentario' },
  creacion:   { Icon: Plus,         chip: 'bg-emerald-100 text-emerald-700', label: 'Creación' },
  cambio:     { Icon: PencilSimple, chip: 'bg-amber-100 text-amber-700',     label: 'Cambio' },
} as const;

function TimelineItem({
  evento, catalogs, showTaskName, onTaskClick, card,
}: {
  evento: EventWithTask;
  catalogs: ReturnType<typeof useCatalogs>['data'];
  showTaskName: boolean;
  onTaskClick?: (taskId: number) => void;
  card: boolean;
}) {
  const meta = TIPO_META[evento.tipo] ?? TIPO_META.cambio;
  const Icon = meta.Icon;

  const header = (
    <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-0.5 flex-wrap">
      <strong className="text-stone-700">{evento.nomActor || 'Usuario desconocido'}</strong>
      {showTaskName && evento.nombreTarea && evento.idTareaArq != null && (
        <>
          <span>en</span>
          <button
            type="button"
            onClick={() => onTaskClick?.(evento.idTareaArq!)}
            className="max-w-[260px] truncate rounded-full bg-stone-100 px-2 py-0.5 font-medium text-brand-green-200 hover:bg-brand-green-100/30 hover:text-brand-green-200"
            title={evento.nombreTarea}
          >
            {evento.nombreTarea}
          </button>
        </>
      )}
      <span
        className="ml-auto font-mono text-[10px] text-stone-400 whitespace-nowrap"
        title={fmtDate(evento.fecha, true)}
      >
        {/* En el feed (card): tiempo relativo para hoy, hora para otros días
            (el día ya va en el encabezado del grupo). En el modal: fecha+hora. */}
        {card
          ? (dayGroupLabel(evento.fecha) === 'Hoy' ? relativeTime(evento.fecha) : fmtTime(evento.fecha))
          : fmtDate(evento.fecha, true)}
      </span>
    </div>
  );

  const body = (
    <>
      {evento.tipo === 'creacion' && (
        <p className="text-xs text-stone-600 italic">Creó la tarea</p>
      )}

      {evento.tipo === 'comentario' && evento.comentario && (
        <div
          className="bg-stone-50 rounded px-3 py-2 text-xs text-stone-800 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: evento.comentario }}
        />
      )}

      {evento.tipo === 'cambio' && evento.cambios && evento.cambios.length > 0 && (
        <div className="space-y-1">
          {evento.cambios.map((c, i) => {
            const before = resolveValue(c.columna, c.valorAnterior, catalogs);
            const after = resolveValue(c.columna, c.valorNuevo, catalogs);
            const label = labelForColumn(c.columna);
            // Color semántico del valor nuevo (Estado/Prioridad). Si no aplica,
            // el pill usa el verde tenue de marca.
            const afterColor = resolveColor(c.columna, c.valorNuevo, catalogs);
            return (
              <div key={`${c.columna}-${i}`} className="text-xs flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-stone-500">{label}</span>
                {card ? (
                  <>
                    {before && (
                      <span className="rounded bg-stone-100 px-1.5 py-0.5 text-stone-400 line-through">
                        {truncate(before)}
                      </span>
                    )}
                    <ArrowRight size={10} weight="bold" className="text-stone-400 shrink-0" />
                    {afterColor ? (
                      <span
                        className="rounded px-1.5 py-0.5 font-medium"
                        style={{ backgroundColor: afterColor, color: contrastingText(afterColor) }}
                      >
                        {truncate(after)}
                      </span>
                    ) : (
                      <span className="rounded bg-brand-green-100/25 px-1.5 py-0.5 font-medium text-stone-800">
                        {truncate(after)}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="font-mono text-stone-500 line-through">{truncate(before)}</span>
                    <ArrowRight size={10} weight="bold" className="text-stone-400 shrink-0" />
                    <span className="font-mono text-stone-800">{truncate(after)}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // Variante card (feed): tarjeta con borde + chip de tipo en el icono.
  if (card) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 transition-shadow hover:shadow-sm">
        <div className="flex gap-2.5">
          <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.chip}`}>
            <Icon size={13} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            {header}
            {body}
          </div>
        </div>
      </div>
    );
  }

  // Variante compacta (modal): icono lateral, sin tarjeta.
  return (
    <div className="flex gap-3 group">
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.chip}`}>
        <Icon size={14} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        {header}
        {body}
      </div>
    </div>
  );
}

function truncate(s: string, max = 80): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
